// The refreshFromServer merge (docs/integrations-contract.md §7a). Pure: no
// React, no I/O, so every rule below is unit-tested in isolation.
//
//   R1  Server rows win.
//   R2  …except rows with an entry in pendingTodoMutations: the local version
//       (present OR absent) stands, because the queued mutation will write
//       through and the server's merge already treats that edit as newer. A
//       queued delete therefore also keeps a server row from resurrecting.
//   R3  …and local-only rows (digit-only temp ids) are always kept.
//   R4  A local server-backed row missing from the server is deleted ONLY when
//       the fetch was a COMPLETE todosPage walk (last page hasMore:false).
//       A partial or failed walk infers nothing: it upserts what arrived.
//   R5  syncState is server-owned. The client never invents one here.
//
// Ordering: rows already on screen keep their place (a background refresh
// must never reshuffle the list under the user's cursor); new server rows are
// appended in the order the server sent them.

import type { Todo, TodoList, TodoPriority, TodoSource, TodoSyncState } from '../types';
import type { BackendTodo } from '../services/todosApi';

/** A local-only todo has a numeric timestamp id (created before backend sync). */
export const isLocalId = (id: string): boolean => /^\d+$/.test(id);

const PRIORITIES: readonly TodoPriority[] = ['Critical', 'High', 'Medium', 'Low'];
const SOURCES: readonly TodoSource[] = ['rhemn', 'google', 'todoist', 'apple'];
const SYNC_STATES: readonly TodoSyncState[] = ['synced', 'pending', 'error'];

const oneOf = <T extends string>(allowed: readonly T[], value: unknown): T | undefined =>
  typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : undefined;

/**
 * BackendTodo → Todo. Unknown source/syncState values (C7: they are Strings)
 * degrade to absent, which reads as 'rhemn' / 'synced': one odd DB value must
 * never break the list.
 */
export const fromBackendTodo = (rt: BackendTodo): Todo => {
  const todo: Todo = {
    id: rt.id,
    text: rt.text,
    completed: rt.completed,
    priority: oneOf(PRIORITIES, rt.priority) ?? 'Medium',
    listId: rt.listId || undefined,
    blockKey: rt.blockKey || undefined,
    // Without this every step flattens to a top-level task on reload.
    parentId: rt.parentId || undefined,
  };
  const source = oneOf(SOURCES, rt.source);
  if (source && source !== 'rhemn') todo.source = source;
  const syncState = oneOf(SYNC_STATES, rt.syncState);
  if (syncState && syncState !== 'synced') todo.syncState = syncState;
  if (rt.dueDate) todo.dueDate = rt.dueDate;
  if (rt.integrationAccountId) todo.integrationAccountId = rt.integrationAccountId;
  return todo;
};

type PendingMutationLike =
  | { type: 'upsert'; input: { id?: string } }
  | { type: 'delete'; id: string };

/** Ids the offline queue still owns (R2). A queued create has no id yet. */
export const pendingTodoIds = (queue: readonly PendingMutationLike[]): Set<string> => {
  const ids = new Set<string>();
  for (const m of queue) {
    if (m.type === 'delete') ids.add(m.id);
    else if (m.input.id) ids.add(m.input.id);
  }
  return ids;
};

const sameTodo = (a: Todo, b: Todo): boolean =>
  a.id === b.id &&
  a.text === b.text &&
  a.completed === b.completed &&
  a.priority === b.priority &&
  a.listId === b.listId &&
  a.blockKey === b.blockKey &&
  a.parentId === b.parentId &&
  a.source === b.source &&
  a.syncState === b.syncState &&
  (a.dueDate ?? null) === (b.dueDate ?? null) &&
  (a.integrationAccountId ?? null) === (b.integrationAccountId ?? null);

export interface MergeInput {
  local: readonly Todo[];
  server: readonly BackendTodo[];
  /** The walk reached hasMore:false. Only then may missing rows be deletes (R4). */
  complete: boolean;
  pendingIds: ReadonlySet<string>;
}

export interface MergeResult {
  todos: Todo[];
  /** false → nothing differs; the caller can skip the state update. */
  changed: boolean;
  /** Server-backed local rows removed by R4, for logging/tests. */
  deletedIds: string[];
}

export const mergeServerTodos = ({ local, server, complete, pendingIds }: MergeInput): MergeResult => {
  const serverById = new Map<string, BackendTodo>();
  for (const row of server) serverById.set(row.id, row);

  const out: Todo[] = [];
  const deletedIds: string[] = [];
  const seen = new Set<string>();
  let changed = false;

  // Pass 1: rows already on screen, in their current order.
  for (const t of local) {
    seen.add(t.id);
    const remote = serverById.get(t.id);
    if (pendingIds.has(t.id) || isLocalId(t.id)) {
      out.push(t); // R2 / R3
      continue;
    }
    if (remote) {
      const next = fromBackendTodo(remote); // R1
      if (sameTodo(t, next)) {
        out.push(t); // keep identity: memoized rows don't re-render
      } else {
        out.push(next);
        changed = true;
      }
      continue;
    }
    if (complete) {
      deletedIds.push(t.id); // R4
      changed = true;
      continue;
    }
    out.push(t); // partial walk: absence proves nothing
  }

  // Pass 2: server rows we don't have yet. A pending id absent locally is a
  // queued delete (R2): don't resurrect it.
  for (const row of server) {
    if (seen.has(row.id) || pendingIds.has(row.id)) continue;
    seen.add(row.id);
    out.push(fromBackendTodo(row));
    changed = true;
  }

  return { todos: changed ? out : [...local], changed, deletedIds };
};

/** An optimistic list (created locally, create not yet confirmed). */
export const isTmpListId = (id: string | undefined | null): boolean =>
  typeof id === 'string' && id.startsWith('tmp-');

export interface ListMergeOptions {
  /**
   * Real list ids this client created AFTER the snapshot was requested. A
   * snapshot read before the create committed does not contain them, and
   * "server wins" would silently drop a list the user just made.
   */
  createdSince?: ReadonlySet<string>;
  /** Ids this client deleted after the snapshot was requested: never resurrected. */
  deletedSince?: ReadonlySet<string>;
  /** Optimistic ids whose create is still in flight: their own response lands them. */
  inFlight?: ReadonlySet<string>;
}

export interface ListMergeResult {
  lists: TodoList[];
  /**
   * Optimistic id → server id, for a stranded optimistic list whose create
   * did reach the server (the response was lost). The caller remaps its todos.
   */
  adopted: Map<string, string>;
}

/** How far a server clock may trail this device's when matching a lost create. */
const ADOPT_SKEW_MS = 2 * 60_000;

const providerOf = (l: TodoList): string | null => l.provider ?? (l.source && l.source !== 'rhemn' ? l.source : null);

/**
 * todoLists is uncapped, so the server's set is complete and wins — except for
 * what the snapshot cannot know about (see ListMergeOptions): optimistic `tmp-`
 * lists, lists created or deleted locally while the snapshot was in flight.
 *
 * A stranded optimistic list (not in flight) is ADOPTED by a server list the
 * client has never seen, with the same name and provider, created no earlier
 * than the optimistic one (less clock skew): that is the lost-response create,
 * and keeping both would show the list twice and invite a duplicate Retry.
 */
export const reconcileServerLists = (
  local: readonly TodoList[],
  server: readonly TodoList[],
  opts: ListMergeOptions = {},
): ListMergeResult => {
  const { createdSince, deletedSince, inFlight } = opts;
  const localIds = new Set(local.map((l) => l.id));
  const serverIds = new Set(server.map((l) => l.id));
  const lists = server.filter((l) => !deletedSince?.has(l.id));
  const adopted = new Map<string, string>();
  const claimed = new Set<string>();

  for (const l of local) {
    if (serverIds.has(l.id)) continue;
    if (isTmpListId(l.id)) {
      if (!inFlight?.has(l.id)) {
        const created = Date.parse(l.createdAt);
        const match = lists.find((s) =>
          !localIds.has(s.id)
          && !claimed.has(s.id)
          && s.name.trim() === l.name.trim()
          && providerOf(s) === providerOf(l)
          && (Number.isNaN(created) || Date.parse(s.createdAt) >= created - ADOPT_SKEW_MS));
        if (match) {
          claimed.add(match.id);
          adopted.set(l.id, match.id);
          continue;
        }
      }
      lists.push(l);
      continue;
    }
    if (createdSince?.has(l.id)) lists.push(l);
  }
  return { lists, adopted };
};

/** Back-compat shape: the reconciled lists only. */
export const mergeServerLists = (
  local: readonly TodoList[],
  server: readonly TodoList[],
  opts?: ListMergeOptions,
): TodoList[] => reconcileServerLists(local, server, opts).lists;
